import React, { useState, useRef, useCallback } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';
import { 
  Mic, MicOff, Upload, Play, Pause, Trash2, 
  FileAudio, Loader2, Volume2, CheckCircle2, 
  AlertCircle, X 
} from 'lucide-react';

const AudioBriefRecorder = ({ onTranscription, theme }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioUrl = URL.createObjectURL(audioBlob);
        setAudioBlob(audioBlob);
        setAudioUrl(audioUrl);
        setUploadedFile(null);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Impossible d\'accéder au microphone. Vérifiez les permissions.');
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['audio/mp3', 'audio/mpeg', 'audio/wav', 'audio/webm', 'audio/m4a', 'audio/mp4', 'audio/x-m4a'];
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|webm|m4a|mp4|mpeg)$/i)) {
      toast.error('Format audio non supporté. Utilisez MP3, WAV, M4A ou WEBM.');
      return;
    }

    // Validate file size (25MB max)
    if (file.size > 25 * 1024 * 1024) {
      toast.error('Le fichier dépasse la limite de 25MB.');
      return;
    }

    setUploadedFile(file);
    setAudioBlob(file);
    setAudioUrl(URL.createObjectURL(file));
    setTranscription('');
  };

  // Trigger file input
  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  // Play/Pause audio
  const togglePlayback = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Transcribe audio
  const transcribeAudio = async () => {
    if (!audioBlob) {
      toast.error('Aucun audio à transcrire');
      return;
    }

    setIsTranscribing(true);
    
    try {
      const formData = new FormData();
      
      if (uploadedFile) {
        formData.append('file', uploadedFile);
      } else {
        // For recorded audio, create a file from the blob
        const file = new File([audioBlob], 'recording.webm', { type: 'audio/webm' });
        formData.append('file', file);
      }

      const response = await api.post('/briefs/transcribe-audio', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        setTranscription(response.data.transcription);
        onTranscription?.(response.data.transcription);
        toast.success('Transcription réussie !');
      } else {
        throw new Error(response.data.detail || 'Erreur de transcription');
      }
    } catch (error) {
      console.error('Transcription error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la transcription');
    } finally {
      setIsTranscribing(false);
    }
  };

  // Clear everything
  const clearAudio = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setUploadedFile(null);
    setTranscription('');
    setIsPlaying(false);
    setRecordingTime(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Audio ended handler
  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  return (
    <Card className="border-2 border-dashed border-slate-200 bg-slate-50/50">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <FileAudio className="h-5 w-5" style={{ color: theme?.colors?.accent || '#0ea5e9' }} />
          <span className="font-medium text-sm">Brief Audio</span>
          <Badge variant="outline" className="text-xs">Optionnel</Badge>
        </div>
        
        <p className="text-xs text-slate-500 mb-4">
          Enregistrez ou importez un fichier audio. L'IA transcrira automatiquement votre brief.
        </p>

        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileUpload}
          accept="audio/mp3,audio/mpeg,audio/wav,audio/webm,audio/m4a,audio/mp4,.mp3,.wav,.webm,.m4a"
          className="hidden"
        />

        {/* Recording/Upload Controls */}
        {!audioUrl && !isRecording && (
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              type="button"
              onClick={startRecording}
              className="flex-1 gap-2"
              style={{ backgroundColor: theme?.colors?.accent || '#0ea5e9' }}
            >
              <Mic className="h-4 w-4" />
              Enregistrer
            </Button>
            <Button
              type="button"
              onClick={triggerFileUpload}
              variant="outline"
              className="flex-1 gap-2"
            >
              <Upload className="h-4 w-4" />
              Importer un fichier
            </Button>
          </div>
        )}

        {/* Recording in progress */}
        {isRecording && (
          <div className="text-center">
            <div className="relative inline-flex items-center justify-center mb-4">
              <div className="absolute w-20 h-20 bg-red-500/20 rounded-full animate-ping" />
              <div className="relative w-16 h-16 bg-red-500 rounded-full flex items-center justify-center">
                <Mic className="h-8 w-8 text-white animate-pulse" />
              </div>
            </div>
            <p className="text-2xl font-mono font-bold text-red-600 mb-2">
              {formatTime(recordingTime)}
            </p>
            <p className="text-sm text-slate-600 mb-4">Enregistrement en cours...</p>
            <Button
              type="button"
              onClick={stopRecording}
              variant="destructive"
              className="gap-2"
            >
              <MicOff className="h-4 w-4" />
              Arrêter
            </Button>
          </div>
        )}

        {/* Audio Preview */}
        {audioUrl && !isRecording && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
              <Button
                type="button"
                onClick={togglePlayback}
                size="icon"
                variant="outline"
                className="h-10 w-10 rounded-full flex-shrink-0"
              >
                {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              </Button>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Volume2 className="h-4 w-4 text-slate-400 flex-shrink-0" />
                  <audio
                    ref={audioRef}
                    src={audioUrl}
                    onEnded={handleAudioEnded}
                    className="w-full"
                    controls
                    style={{ height: '32px' }}
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1 truncate">
                  {uploadedFile ? uploadedFile.name : `Enregistrement (${formatTime(recordingTime)})`}
                </p>
              </div>
              
              <Button
                type="button"
                onClick={clearAudio}
                size="icon"
                variant="ghost"
                className="h-8 w-8 text-slate-400 hover:text-red-500 flex-shrink-0"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            {/* Transcribe Button */}
            {!transcription && (
              <Button
                type="button"
                onClick={transcribeAudio}
                disabled={isTranscribing}
                className="w-full gap-2"
                style={{ backgroundColor: theme?.colors?.accent || '#0ea5e9' }}
              >
                {isTranscribing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Transcription en cours...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4" />
                    Transcrire l'audio
                  </>
                )}
              </Button>
            )}

            {/* Transcription Result */}
            {transcription && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <span className="text-sm font-medium text-green-700">Transcription</span>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setTranscription('');
                      onTranscription?.('');
                    }}
                    className="h-6 px-2 text-xs"
                  >
                    <X className="h-3 w-3 mr-1" />
                    Effacer
                  </Button>
                </div>
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{transcription}</p>
                </div>
                <p className="text-xs text-slate-500">
                  Cette transcription sera ajoutée à votre brief.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Help text */}
        {!audioUrl && !isRecording && (
          <p className="text-xs text-slate-400 mt-3 text-center">
            Formats supportés: MP3, WAV, M4A, WEBM • Max 25MB
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default AudioBriefRecorder;
